import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useToastStore } from '@/components/Toaster/store'
import { useConfig } from '@/composables/config'
import { useTr } from '@/composables/use-tr'
import { PWA_APP_CLIDS } from '@/constants/general'
import { useAppStore } from '@/store/app-store'
import { useClientAppStore } from '@/store/client-app-store'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'

import toastImage1 from './images/image-1.webp'
import toastImage2 from './images/image-2.webp'
import toastImage3 from './images/image-3.webp'

const PUSH_SUBSCRIPTION_PROMPT_CONFIG = {
    MIN_VISITS: 2,
    MAX_DISPLAY_COUNT: 100,
    DISPLAY_DELAY_HOURS: 24 * 7,
} as const

export const usePushSubscriptionStore = defineStore(
    'push_subscription',
    () => {
        const { VUE_APP_ONESIGNAL_APPID } = useConfig()
        const tr = useTr()
        const toastStore = useToastStore()
        const layoutStore = useLayoutStore()
        const loggerStore = useLoggerStore()
        const appStore = useAppStore()
        const clientAppStore = useClientAppStore()

        const properEnv = computed(() => appStore.platform === 'desktop' && appStore.isPWA)

        const displayCount = ref(0)
        const lastDisplayTime = ref(0)
        const sessions = ref(0)

        const shouldShowPrompt = computed(() => {
            if (import.meta.env.SSR) {
                return false
            }

            if (!properEnv.value) {
                return false
            }

            if (layoutStore.fullScreen) {
                return false
            }

            if (Notification.permission !== 'default') {
                return false
            }

            if ('serviceWorker' in navigator === false) {
                return false
            }

            if (displayCount.value >= PUSH_SUBSCRIPTION_PROMPT_CONFIG.MAX_DISPLAY_COUNT) {
                return false
            }

            if (
                Date.now() - lastDisplayTime.value <
                PUSH_SUBSCRIPTION_PROMPT_CONFIG.DISPLAY_DELAY_HOURS * 60 * 60 * 1000
            ) {
                return false
            }

            return true
        })

        const toastConfig = computed(() => {
            return [
                {
                    image: toastImage1,
                    title: tr('toast-push-subscription.title1'),
                    description: tr('toast-push-subscription.description1'),
                    buttonText: tr('toast-push-subscription.button1'),
                },
                {
                    image: toastImage2,
                    title: tr('toast-push-subscription.title2'),
                    description: tr('toast-push-subscription.description2'),
                    buttonText: tr('toast-push-subscription.button2'),
                },
                {
                    image: toastImage3,
                    title: tr('toast-push-subscription.title3'),
                    description: tr('toast-push-subscription.description3'),
                    buttonText: tr('toast-push-subscription.button3'),
                },
            ][displayCount.value % 3]!
        })

        const showSubscriptionPrompt = ({
            trigger,
            considerMinVisits,
        }: {
            trigger: string
            considerMinVisits?: boolean
        }) => {
            if (considerMinVisits && sessions.value < PUSH_SUBSCRIPTION_PROMPT_CONFIG.MIN_VISITS) {
                return
            }

            if (!shouldShowPrompt.value) {
                return
            }

            const variant = String(displayCount.value % 3)

            toastStore.showToast({
                type: 'push-subscription-prompt',
                trigger,
                variant,
                title: toastConfig.value.title,
                description: toastConfig.value.description,
                image: toastConfig.value.image,
                buttonText: toastConfig.value.buttonText,
                weight: 1,
                imageStyles: { height: '120rem' },
                onAccept: () => {
                    window.OneSignalDeferred.push(async (OneSignal) => {
                        await OneSignal.Notifications.requestPermission()
                        const result = OneSignal.Notifications.permissionNative
                        if (result === 'granted') {
                            loggerStore.logEvent({
                                event: 'custom_event',
                                eventName: 'push_notification_granted',
                                dimension1: 'push-subscription-prompt',
                                dimension3: variant,
                            })
                        } else if (result === 'denied') {
                            loggerStore.logEvent({
                                event: 'custom_event',
                                eventName: 'push_notification_denied',
                                dimension1: 'push-subscription-prompt',
                                dimension3: variant,
                            })
                        }
                    })
                },
                onShow: () => {
                    displayCount.value += 1
                    lastDisplayTime.value = Date.now()

                    loggerStore.logInterfaceEvents({
                        interface_target: 'push-subscription-prompt-toast',
                        eventName: 'interface_show',
                    })
                },
                isFullScreenAllowed: false,
            })
        }

        const init = () => {
            if (properEnv.value) {
                window.OneSignalDeferred.push(async (OneSignal) => {
                    await OneSignal.init({
                        appId: VUE_APP_ONESIGNAL_APPID,
                        notifyButton: {
                            enable: false,
                        },
                        welcomeNotification: {
                            disable: true,
                        },
                        autoResubscribe: true,
                        serviceWorkerPath: `OneSignalSDKWorker.js`,
                        serviceWorkerParam: { scope: '/' },
                    })

                    OneSignal.User.addTags({
                        url_scope: `${location.origin + location.pathname}?clid=${PWA_APP_CLIDS.pwa}`,
                    })
                    if (clientAppStore.auid) {
                        await OneSignal.login(clientAppStore.auid)
                    }
                })

                sessions.value += 1
            }
        }

        return {
            init,
            showSubscriptionPrompt,
            displayCount,
            lastDisplayTime,
            sessions,
            properEnv,
        }
    },
    {
        persist: {
            key: 'push_subscribe',
            pick: ['displayCount', 'lastDisplayTime', 'sessions'],
        },
    },
)
